import { QueryClient, queryOptions } from '@tanstack/react-query';

import { UserState } from '@/redux/slices/user/types';
import { buildURL, callDelete, patch, post, RequestError } from '@/shared/fetch';

import { getQueryFn } from '../utils';
import {
  GetUserOfferResponse,
  GetUserProfileResponse,
  HabitatOnboardingRequest,
  PatchUserProfileRequest,
  PatchUserRequest,
  PostUserPasswordRequest,
  TourOnboardingRequest,
  UpdateMarketingOptInRequest,
} from './types';

/**
 * An ODTP on the URL is redeemed by the server as a side effect of reading the user, so it has to
 * ride on this request — there is no separate redeem endpoint.
 *
 * It is consumed once and then forgotten, which is why it cannot simply be baked into the query.
 * The key stays `['users', 'me']` whether or not a token was present, so every reader shares one
 * cache entry; putting the token in the key would fork the cache and leave the app watching an
 * entry nothing else writes. Refetches after the first therefore carry no token, which is correct:
 * it is a one-time redemption, not a property of the user.
 */
let pendingOdtpParam: string | null = null;

export const setPendingOdtpParam = (param: string | null) => {
  pendingOdtpParam = param;
};

const consumePendingOdtpParam = () => {
  const param = pendingOdtpParam;
  pendingOdtpParam = null;
  return param;
};

/**
 * Put an unspent token back, so the next read carries it again.
 *
 * A request the API never answered has not redeemed anything, and the token is a paid-for day
 * ticket — dropping it silently is the one outcome that costs the visitor something real.
 * Cancellation is the case that matters: `invalidateUser` cancels the in-flight read on purpose,
 * and that read had already taken the token off this variable on its way out.
 *
 * Only for failures with no server response behind them. A `RequestError` means the API did answer,
 * and therefore did see the token; presenting it again would be a second redemption attempt, and
 * whether that is safe is the server's contract to state, not this side's to assume.
 */
const restorePendingOdtpParam = (param: string | null, error: unknown) => {
  if (param && !pendingOdtpParam && !(error instanceof RequestError)) {
    pendingOdtpParam = param;
  }
};

/** The session query's key, exported so logging out can retire the cached answer with it. */
export const USER_QUERY_KEY = ['users', 'me'] as const;

/**
 * Re-read the session after something has changed it on the server.
 *
 * `UserSessionSync` owns this query and redux is derived from it, so anything that patches redux
 * directly — verifying an address, say — is describing a change the query has not heard about. Left
 * alone, a fetch already in flight answers with the state from *before* the change and overwrites
 * the patch: which is exactly what made a verification link land on a page still asking for the
 * code it had just accepted.
 */
export const invalidateUser = async (queryClient: QueryClient) => {
  // Cancelled first, because invalidating on its own does nothing while a fetch is already in
  // flight: react-query dedupes against it, and the answer already on its way is the one from
  // before the change — which is precisely the fetch that needs discarding. Cancelling drops that
  // result, so the refetch below is the one that lands.
  await queryClient.cancelQueries({ queryKey: USER_QUERY_KEY });
  return queryClient.refetchQueries({ queryKey: USER_QUERY_KEY });
};

export const getUserQueryOptions = () =>
  queryOptions({
    queryKey: USER_QUERY_KEY,
    // Built per call, not once: the URL depends on whether a token is still outstanding.
    queryFn: async (context) => {
      const odtp = consumePendingOdtpParam();
      try {
        return await getQueryFn<UserState | null>(`/users/user${odtp ? `?${odtp}` : ''}`)(context);
      } catch (err) {
        // Hand the token back when nothing answered — a cancelled read, or a dead network — rather
        // than burning a day ticket on a request the API never processed.
        restorePendingOdtpParam(odtp, err);
        throw err;
      }
    },
  });

export const getHostsQueryOptions = () =>
  queryOptions({
    queryKey: ['users', 'hosts'],
    queryFn: getQueryFn<{ hosts: string[] }>('/admin/users/hosts'),
    select: ({ hosts }) => hosts,
  });

export const getUserProfileQueryOptions = (username: string | undefined) => {
  return queryOptions({
    queryKey: ['users', 'profile', username],
    queryFn: getQueryFn<GetUserProfileResponse>(`/users/profile/${username}`),
    enabled: !!username,
    retry: (_failureCount: number, error: any) => error.statusCode !== 404,
  });
};

/**
 * Whether the current user may redeem an offer. The offer id is case-insensitive on the backend,
 * so we lowercase it here to keep a stable cache key and URL regardless of how it was authored.
 */
export const getUserOfferQueryOptions = (offerId: string | undefined) => {
  const id = offerId?.toLowerCase();
  return queryOptions({
    queryKey: ['users', 'me', 'offers', id],
    queryFn: getQueryFn<GetUserOfferResponse>(`/users/me/offers/${id}`),
    enabled: !!id,
  });
};

export const patchUserProfileMutationFn = async (args: PatchUserProfileRequest) => {
  const { timeZone: timezone } = Intl.DateTimeFormat().resolvedOptions();
  return patch(buildURL('/users/profile'), {
    ...args,
    timezone,
  });
};

export const postUserMarketingOptInMutationFn = async (args: UpdateMarketingOptInRequest) => {
  return post(buildURL('/users/account/marketing'), args);
};

export const postUserAppOnboardedMutationFn = async () => {
  return post(buildURL('/users/steps'), { step: 'isAppOnboarded', value: true });
};

export const postUserWelcomeMessageShownMutationFn = async () => {
  return post<Pick<UserState, 'isWelcomeMessageShown'>>(buildURL('/users/steps'), {
    step: 'isWelcomeMessageShown',
    value: true,
  });
};

export const postHabitatOnboardingMutationFn = async (args: HabitatOnboardingRequest) => {
  return post<{ user: Partial<UserState> }>(buildURL('/users/onboarding'), args);
};

export const patchUserMutationFn = async (args: PatchUserRequest) => {
  return patch<Partial<UserState>>(buildURL('/users/me'), args);
};

/**
 * Asks the server to email the current user their Profile Completed Reward code.
 *
 * Takes no arguments on purpose: the endpoint sends to the authenticated session's own address and
 * accepts no recipient, which is the entire reason it is safe to call without a server-side
 * eligibility check (zl-api ADR 0004). Nothing is recorded, so calling it again just sends again.
 */
export const postProfileCompletedRewardMutationFn = async () => {
  return post(buildURL('/email/profile-completed-reward'), {});
};

export const postTourOnboardingMutationFn = async (args: TourOnboardingRequest) => {
  return post<{ user: Partial<UserState> }>(buildURL('/users/tourOnboarding'), args);
};

export const postRedeemCode = async (code: string) => {
  return post<{ user: Partial<UserState>; productId: string; price: number; status: string }>(buildURL('/redeem'), { code });
};

export const postUserPasswordMutationFn = async (args: PostUserPasswordRequest) => {
  return post<{ user: Partial<UserState> }>(buildURL('/users/account/password'), args);
};

export const postResetPasswordMutationFn = async (email: string) => {
  return post(buildURL('/users/password/reset'), {
    email,
    origin: window.location.origin,
  });
};

export const deleteUserMutationFn = () => callDelete(buildURL('/users/account/delete'));
